import React, { createContext, useCallback, useContext, useRef } from 'react';
import { toast } from 'sonner';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useAuth } from '@/context/AuthContext';

const WebSocketContext = createContext(null);

export function useWebSocketContext() {
  const ctx = useContext(WebSocketContext);
  if (!ctx) throw new Error('useWebSocketContext must be used within WebSocketProvider');
  return ctx;
}

/**
 * Single app-level WebSocket connection.
 *
 * - Shows toast notifications globally (import progress, wish-list).
 * - Provides subscribe(fn) so individual pages can react to events
 *   without maintaining their own WS connection.
 */
export function WebSocketProvider({ children }) {
  const { token } = useAuth();
  const subscribersRef = useRef(new Set());
  const lastSegmentRef = useRef('');
  const lastStatusRef = useRef('');

  /** Register a callback that receives every WS message. Returns unsubscribe fn. */
  const subscribe = useCallback((fn) => {
    subscribersRef.current.add(fn);
    return () => subscribersRef.current.delete(fn);
  }, []);

  const handleMessage = useCallback((msg) => {
    // ── Global import-progress notifications ──
    if (msg.type === 'import_progress') {
      const seg = msg.segment || '';
      const status = msg.status || '';

      if (seg && seg !== lastSegmentRef.current) {
        lastSegmentRef.current = seg;
        toast.info(`Загружается сегмент: ${seg}`, { duration: 4000 });
      }
      if (status === 'completed' && lastStatusRef.current !== 'completed') {
        toast.success(`Импорт завершён: ${msg.current ?? 0} товаров обработано`);
        lastSegmentRef.current = '';   // reset for next import
      }
      if (status === 'error' && lastStatusRef.current !== 'error') {
        toast.error('Импорт Интерком завершился с ошибкой');
      }
      lastStatusRef.current = status;
    }

    // ── Wish-list notification ──
    if (msg.type === 'wish_list_available') {
      const { count = 0, products: prods = [] } = msg;
      const names = prods.slice(0, 3).map((p) => p.name).join(', ');
      const extra = count > 3 ? ` и ещё ${count - 3}` : '';
      toast.success(`🔔 Появились товары из ожидаемых: ${names}${extra}`, { duration: 8000 });
    }

    if (msg.type === 'buy_later_due') {
      const { count = 0, products: prods = [] } = msg;
      const names = prods.slice(0, 3).map((p) => p.name).join(', ');
      const extra = count > 3 ? ` и ещё ${count - 3}` : '';
      toast.success(`⏰ Пора купить: ${names}${extra}`, { duration: 10000 });
    }

    // Dispatch to page-level subscribers
    subscribersRef.current.forEach((fn) => {
      try { fn(msg); } catch {}
    });
  }, []);

  const { connected } = useWebSocket(token, handleMessage);

  return (
    <WebSocketContext.Provider value={{ connected, subscribe }}>
      {children}
    </WebSocketContext.Provider>
  );
}
